import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Icon } from '../../components/Icon.js'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
} from '../../components/ui.js'
import { money } from '../../lib/format.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'

/**
 * The services the business sells, and what each one puts on a document.
 *
 * `ServiceType` has been seeded and read-only since it landed, which is why
 * this screen did not exist. It exists now for one reason: **raising an
 * invoice should not mean typing the same four lines again.** The lines
 * configured against a service are offered when a document is raised on a job
 * carrying it — offered, and then edited, because the two-bedroom unit and the
 * double-storey rendered house are the same service at different prices.
 *
 * Three things worth knowing about the shape:
 *
 * - **A blank price means "the catalog price on the day".** A line pointing at
 *   a `Material` with no price of its own follows that material, so a price
 *   rise reaches every service quoting it instead of being copied into a dozen
 *   rows that then disagree. A free-text line has no material to follow and so
 *   must carry a price; the server refuses one without.
 * - **Remove is retire.** A line taken off the list is still on the documents
 *   already raised from it, the same call made for a job category (rule 23).
 * - **Hazards and required licences are the safety half**, under the lines
 *   when a service is open: the hazards a technician reads before going in,
 *   and the tickets the work needs, which is what the job card's licence
 *   banner checks. Both were seed data; a new hazard used to be a deploy.
 */

type ServiceType = RouterOutputs['catalog']['serviceTypes'][number]
type ServiceLine = ServiceType['defaultLines'][number]

export function ServicesCard() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [showRetired, setShowRetired] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const services = useQuery(
    trpc.catalog.serviceTypes.queryOptions({ includeArchived: showRetired }),
  )
  const taxRates = useQuery(trpc.catalog.taxRates.queryOptions())

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.catalog.pathFilter())
    // A job card reads a service's default price, so the catalogue reaching it
    // matters as much as this screen redrawing.
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const upsertService = useMutation(
    trpc.catalog.upsertServiceType.mutationOptions({
      onSuccess: () => {
        invalidate()
        setAdding(false)
      },
    }),
  )
  const archiveService = useMutation(
    trpc.catalog.archiveServiceType.mutationOptions({ onSuccess: invalidate }),
  )

  return (
    <Card>
      <CardHeader
        title="Services"
        action={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowRetired((current) => !current)}
            >
              {showRetired ? 'Hide retired' : 'Show retired'}
            </Button>
            <Button size="sm" onClick={() => setAdding((current) => !current)}>
              Add a service
            </Button>
          </div>
        }
      />

      <div className="px-[var(--row-pad-x)] py-3">
        <p className="mb-3 max-w-[560px] text-xs text-ink-500">
          The lines on a service are offered when a quote or an invoice is
          raised on a job that carries it. Nothing is filled in automatically —
          the office picks what applies and edits the figures.
        </p>

        {adding ? (
          <NewService
            onCancel={() => setAdding(false)}
            onSubmit={(name) => upsertService.mutate({ name })}
            saving={upsertService.isPending}
            error={upsertService.error ? errorMessage(upsertService.error) : null}
          />
        ) : null}

        {services.isPending ? (
          <SkeletonRows rows={4} />
        ) : services.error ? (
          <p className="text-[13px] text-red-700">{errorMessage(services.error)}</p>
        ) : services.data.length === 0 ? (
          <EmptyState
            title="No services"
            description="Add the kinds of work the business sells, then give each one the lines it bills."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-ink-100">
            {services.data.map((service) => (
              <ServiceRow
                key={service.id}
                service={service}
                taxRates={taxRates.data ?? []}
                expanded={open === service.id}
                onToggle={() =>
                  setOpen((current) => (current === service.id ? null : service.id))
                }
                onRetire={(archived) =>
                  archiveService.mutate({ id: service.id, archived })
                }
                onInvalidate={invalidate}
              />
            ))}
          </ul>
        )}
      </div>
    </Card>
  )
}

function NewService({
  onCancel,
  onSubmit,
  saving,
  error,
}: {
  onCancel: () => void
  onSubmit: (name: string) => void
  saving: boolean
  error: string | null
}) {
  const [name, setName] = useState('')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (name.trim() === '') return
    onSubmit(name.trim())
    setName('')
  }

  return (
    <form onSubmit={submit} className="mb-3 flex flex-col gap-2 rounded-md bg-ink-50 p-3">
      <Field label="What is the service called" htmlFor="new-service-name">
        <Input
          id="new-service-name"
          value={name}
          autoFocus
          placeholder="Annual termite inspection"
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" variant="primary" loading={saving}>
          Add it
        </Button>
      </div>
    </form>
  )
}

function ServiceRow({
  service,
  taxRates,
  expanded,
  onToggle,
  onRetire,
  onInvalidate,
}: {
  service: ServiceType
  taxRates: RouterOutputs['catalog']['taxRates']
  expanded: boolean
  onToggle: () => void
  onRetire: (archived: boolean) => void
  onInvalidate: () => void
}) {
  const retired = service.archivedAt !== null
  const lines = service.defaultLines

  return (
    <li className="py-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <Icon
            name="chevronDown"
            strokeWidth={2.25}
            className={`size-3.5 shrink-0 text-ink-400 transition-transform ${expanded ? '' : '-rotate-90'}`}
          />
          <span className="truncate text-[13px] font-medium text-ink-900">{service.name}</span>
          {retired ? <Badge tone="neutral">Retired</Badge> : null}
          <span className="ml-auto shrink-0 text-xs text-ink-500">
            {lines.length === 0
              ? 'No lines'
              : lines.length === 1
                ? '1 line'
                : `${lines.length} lines`}
            {service.hazards.length > 0
              ? ` · ${service.hazards.length} ${service.hazards.length === 1 ? 'hazard' : 'hazards'}`
              : ''}
          </span>
        </button>
        <Button size="sm" variant="ghost" onClick={() => onRetire(!retired)}>
          {retired ? 'Bring back' : 'Retire'}
        </Button>
      </div>

      {expanded ? (
        <>
          <ServiceLines
            serviceTypeId={service.id}
            lines={lines}
            taxRates={taxRates}
            onInvalidate={onInvalidate}
          />
          <ServiceSafety service={service} onInvalidate={onInvalidate} />
        </>
      ) : null}
    </li>
  )
}

function ServiceLines({
  serviceTypeId,
  lines,
  taxRates,
  onInvalidate,
}: {
  serviceTypeId: string
  lines: ServiceLine[]
  taxRates: RouterOutputs['catalog']['taxRates']
  onInvalidate: () => void
}) {
  const trpc = useTRPC()
  const [adding, setAdding] = useState(false)

  const upsert = useMutation(
    trpc.catalog.upsertServiceLine.mutationOptions({
      onSuccess: () => {
        onInvalidate()
        setAdding(false)
      },
    }),
  )
  const remove = useMutation(
    trpc.catalog.removeServiceLine.mutationOptions({ onSuccess: onInvalidate }),
  )

  return (
    <div className="mt-2 ml-5 flex flex-col gap-2 border-l border-ink-100 pl-4">
      {lines.length === 0 ? (
        <p className="text-xs text-ink-400">
          Nothing configured. A job with this service still gets one line, from
          the price on the job.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {lines.map((line) => (
            <li key={line.id} className="flex items-center gap-2 text-[13px]">
              <span className="min-w-0 flex-1 truncate text-ink-800">{line.description}</span>
              <span className="shrink-0 tabular-nums text-ink-600">
                {line.quantity.toString()} ×
              </span>
              <span className="shrink-0 tabular-nums text-ink-800">
                {line.unitPrice === null ? (
                  <span className="text-ink-500">catalog price</span>
                ) : (
                  money(line.unitPrice)
                )}
              </span>
              <span className="w-[76px] shrink-0 truncate text-right text-xs text-ink-500">
                {line.taxRate?.name ?? 'No tax'}
              </span>
              <button
                type="button"
                aria-label={`Remove ${line.description}`}
                onClick={() => remove.mutate({ id: line.id })}
                className="flex size-6 items-center justify-center text-ink-400 hover:text-ink-800"
              >
                <Icon name="close" className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <NewLine
          taxRates={taxRates}
          saving={upsert.isPending}
          error={upsert.error ? errorMessage(upsert.error) : null}
          onCancel={() => setAdding(false)}
          onSubmit={(line) =>
            upsert.mutate({
              serviceTypeId,
              description: line.description,
              quantity: line.quantity,
              unitPrice: line.unitPrice,
              taxRateId: line.taxRateId,
              sortOrder: lines.length,
            })
          }
        />
      ) : (
        <div>
          <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>
            Add a line
          </Button>
        </div>
      )}

      {remove.error ? (
        <p className="text-xs text-red-700">{errorMessage(remove.error)}</p>
      ) : null}
    </div>
  )
}

function NewLine({
  taxRates,
  saving,
  error,
  onCancel,
  onSubmit,
}: {
  taxRates: RouterOutputs['catalog']['taxRates']
  saving: boolean
  error: string | null
  onCancel: () => void
  onSubmit: (line: {
    description: string
    quantity: string
    unitPrice: number
    taxRateId: string | null
  }) => void
}) {
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [price, setPrice] = useState('')
  const [taxRateId, setTaxRateId] = useState(
    taxRates.find((rate) => rate.isDefault)?.id ?? taxRates[0]?.id ?? '',
  )

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (description.trim() === '') return

    const dollars = Number(price.trim())

    onSubmit({
      description: description.trim(),
      quantity: quantity.trim() === '' ? '1' : quantity.trim(),
      // A free-text line has no catalog item to follow, so a blank price is a
      // zero rather than "whatever it costs". The catalog-item case arrives
      // with the picker, which is still a later screen.
      unitPrice: Number.isFinite(dollars) ? Math.round(dollars * 100) : 0,
      taxRateId: taxRateId === '' ? null : taxRateId,
    })
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2 rounded-md bg-ink-50 p-2.5">
      <div className="grid gap-2 sm:grid-cols-[1fr_70px_100px_120px]">
        <Field label="Description" htmlFor="new-line-description">
          <Input
            id="new-line-description"
            value={description}
            autoFocus
            placeholder="Termite inspection — standard"
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <Field label="Qty" htmlFor="new-line-quantity">
          <Input
            id="new-line-quantity"
            inputMode="decimal"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </Field>
        <Field label="Unit $" htmlFor="new-line-price">
          <Input
            id="new-line-price"
            inputMode="decimal"
            value={price}
            placeholder="0.00"
            onChange={(event) => setPrice(event.target.value)}
          />
        </Field>
        <Field label="Tax" htmlFor="new-line-tax">
          <Select
            id="new-line-tax"
            value={taxRateId}
            onChange={(event) => setTaxRateId(event.target.value)}
          >
            <option value="">No tax</option>
            {taxRates.map((rate) => (
              <option key={rate.id} value={rate.id}>
                {rate.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {error ? <p className="text-xs text-red-700">{error}</p> : null}

      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" variant="primary" loading={saving}>
          Add the line
        </Button>
      </div>
    </form>
  )
}

/**
 * The safety half of a service: hazards read before going in, and the
 * tickets the work needs.
 */
function ServiceSafety({
  service,
  onInvalidate,
}: {
  service: ServiceType
  onInvalidate: () => void
}) {
  const trpc = useTRPC()
  const licenceTypes = useQuery(trpc.licences.types.queryOptions())

  const add = useMutation(trpc.catalog.addServiceHazard.mutationOptions({ onSuccess: onInvalidate }))
  const remove = useMutation(
    trpc.catalog.removeServiceHazard.mutationOptions({ onSuccess: onInvalidate }),
  )
  const setLicence = useMutation(
    trpc.catalog.setServiceLicence.mutationOptions({ onSuccess: onInvalidate }),
  )

  const [title, setTitle] = useState('')
  const [control, setControl] = useState('')
  const [severity, setSeverity] = useState<'CAUTION' | 'CRITICAL'>('CAUTION')

  const required = new Set(service.licences.map((licence) => licence.licenceType.id))

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!title.trim() || !control.trim()) return
    add.mutate(
      { serviceTypeId: service.id, title: title.trim(), control: control.trim(), severity },
      {
        onSuccess: () => {
          setTitle('')
          setControl('')
          setSeverity('CAUTION')
        },
      },
    )
  }

  return (
    <div className="mt-3 ml-5 flex flex-col gap-2 border-l border-ink-100 pl-4">
      <p className="text-[11px] font-semibold tracking-wider text-ink-500 uppercase">
        Safety · read before going in
      </p>

      {service.hazards.length === 0 ? (
        <p className="text-xs text-ink-400">No hazards recorded for this work.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {service.hazards.map((hazard) => (
            <li
              key={hazard.id}
              className={
                hazard.severity === 'CRITICAL'
                  ? 'flex items-start gap-2 rounded-md bg-red-50 px-2.5 py-2 text-xs text-red-900 ring-1 ring-red-200 ring-inset'
                  : 'flex items-start gap-2 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-900 ring-1 ring-amber-200 ring-inset'
              }
            >
              <span className="min-w-0 flex-1">
                <span className="block font-semibold">{hazard.title}</span>
                <span className="block">{hazard.control}</span>
              </span>
              <button
                type="button"
                aria-label={`Remove ${hazard.title}`}
                onClick={() => remove.mutate({ id: hazard.id })}
                className="flex size-6 shrink-0 items-center justify-center rounded hover:bg-white/70"
              >
                <Icon name="close" className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex flex-col gap-2 rounded-md bg-ink-50 p-2.5">
        <div className="grid gap-2 sm:grid-cols-[1fr_120px]">
          <Field label="Hazard" htmlFor={`hazard-title-${service.id}`}>
            <Input
              id={`hazard-title-${service.id}`}
              value={title}
              placeholder="Confined space"
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          <Field label="How serious" htmlFor={`hazard-severity-${service.id}`}>
            <Select
              id={`hazard-severity-${service.id}`}
              value={severity}
              onChange={(event) => setSeverity(event.target.value as 'CAUTION' | 'CRITICAL')}
            >
              <option value="CAUTION">Caution</option>
              <option value="CRITICAL">Critical</option>
            </Select>
          </Field>
        </div>
        <Field label="What to do about it" htmlFor={`hazard-control-${service.id}`}>
          <Input
            id={`hazard-control-${service.id}`}
            value={control}
            placeholder="Two people; one stays at the hatch."
            onChange={(event) => setControl(event.target.value)}
          />
        </Field>
        {add.error ? <p className="text-xs text-red-700">{errorMessage(add.error)}</p> : null}
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            loading={add.isPending}
            disabled={!title.trim() || !control.trim()}
          >
            Add the hazard
          </Button>
        </div>
      </form>

      {licenceTypes.data && licenceTypes.data.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-medium text-ink-700">Tickets this work needs</p>
          <div className="flex flex-wrap gap-1.5">
            {licenceTypes.data.map((licence) => {
              const on = required.has(licence.id)
              return (
                <button
                  key={licence.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setLicence.mutate({
                      serviceTypeId: service.id,
                      licenceTypeId: licence.id,
                      required: !on,
                    })
                  }
                  className={
                    on
                      ? 'rounded-full bg-brand-800 px-2.5 py-1 text-[11px] font-medium text-white'
                      : 'rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-ink-600 ring-1 ring-ink-300 ring-inset hover:bg-ink-50'
                  }
                >
                  {licence.name}
                </button>
              )
            })}
          </div>
          <p className="text-[11px] text-ink-400">
            The job card warns when the person on the job does not hold these.
          </p>
        </div>
      ) : null}
    </div>
  )
}
